import React, { useEffect, useState, useRef } from "react";
import { Box, Card, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import "./OnboardingTour.css";

const OnboardingTour = () => {
  const {
    steps,
    activeStep,
    isOnboardingActive,
    nextStep,
    prevStep,
    skipTour,
  } = useOnboarding();

  const [targetRect, setTargetRect] = useState(null);
  const [elementVisible, setElementVisible] = useState(false);
  const tooltipRef = useRef(null);

  const currentStep = steps[activeStep];

  const resolveSelector = (step) => {
    if (!step) return "";
    let selector = step.target;
    if (step.id === "sharePost") {
      // Fallback hierarchy if first post is not shareable
      if (!document.querySelector(selector)) {
        selector = ".share-thoughts-btn";
      }
      if (!document.querySelector(selector)) {
        selector = ".postCard";
      }
      if (!document.querySelector(selector)) {
        selector = ".postContentContainer";
      }
    }
    return selector;
  };

  useEffect(() => {
    if (!isOnboardingActive || steps.length === 0 || !currentStep) {
      setTargetRect(null);
      setElementVisible(false);
      return;
    }

    setElementVisible(false);
    setTargetRect(null);

    const updateRect = () => {
      const selector = resolveSelector(currentStep);
      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const viewH = window.innerHeight || document.documentElement.clientHeight;
        const viewW = window.innerWidth || document.documentElement.clientWidth;
        // Mark visible if element at least partially intersects the viewport
        const intersectsViewport =
          rect.top < viewH &&
          rect.bottom > 0 &&
          rect.left < viewW &&
          rect.right > 0;

        if (intersectsViewport) {
          setTargetRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          setElementVisible(true);
          return true;
        }
      }
      return false;
    };

    // Run first check and scroll the element into view if found
    const selector = resolveSelector(currentStep);
    const initialEl = document.querySelector(selector);
    if (initialEl) {
      initialEl.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(updateRect, 350);
    } else {
      updateRect();
    }

    // Set up MutationObserver to react immediately when target elements mount (e.g. posts loading)
    const observer = new MutationObserver(() => {
      const found = updateRect();
      if (found && initialEl === null) {
        const el = document.querySelector(selector);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleResizeOrScroll = () => {
      updateRect();
    };

    window.addEventListener("resize", handleResizeOrScroll);
    // Use capture phase (true) so we intercept scroll events on any scrollable child container (e.g. .postContentContainer)
    window.addEventListener("scroll", handleResizeOrScroll, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll, true);
    };
  }, [activeStep, currentStep, isOnboardingActive, steps.length]);

  if (!isOnboardingActive || steps.length === 0 || !currentStep || !elementVisible) {
    return null;
  }

  // Calculate dynamic position of tooltip card relative to target element using viewport coordinates
  const getTooltipStyle = () => {
    if (!targetRect) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "350px",
        zIndex: 100001,
      };
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spacing = 16;
    const tooltipWidth = 350;
    const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 210;

    // Helper: compute tooltip top/left for a given placement
    const computePosition = (placement) => {
      let top = 0;
      let left = 0;
      switch (placement) {
        case "bottom":
          top = targetRect.top + targetRect.height + spacing;
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
          break;
        case "top":
          top = targetRect.top - tooltipHeight - spacing;
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
          break;
        case "right":
          top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
          left = targetRect.left + targetRect.width + spacing;
          break;
        case "left":
          top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
          left = targetRect.left - tooltipWidth - spacing;
          break;
        default:
          top = targetRect.top + targetRect.height + spacing;
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      }
      // Clamp within viewport
      left = Math.max(spacing, Math.min(left, viewportWidth - tooltipWidth - spacing));
      top = Math.max(spacing, Math.min(top, viewportHeight - tooltipHeight - spacing));
      return { top, left };
    };

    // Helper: check if tooltip rect overlaps with target spotlight rect
    const overlapsTarget = (top, left) => {
      const tRight = left + tooltipWidth;
      const tBottom = top + tooltipHeight;
      const sLeft = targetRect.left - 8;
      const sTop = targetRect.top - 8;
      const sRight = targetRect.left + targetRect.width + 8;
      const sBottom = targetRect.top + targetRect.height + 8;
      return !(tRight <= sLeft || left >= sRight || tBottom <= sTop || top >= sBottom);
    };

    // Helper: check if tooltip fits within viewport
    const fitsInViewport = (top, left) =>
      top >= spacing &&
      left >= spacing &&
      top + tooltipHeight <= viewportHeight - spacing &&
      left + tooltipWidth <= viewportWidth - spacing;

    // Priority order: preferred placement first, then alternatives
    const preferred = currentStep.placement || "bottom";
    const placements = [preferred, ...["bottom", "top", "right", "left"].filter(p => p !== preferred)];

    // Try each placement — pick first that fits AND doesn't overlap
    for (const placement of placements) {
      const { top, left } = computePosition(placement);
      if (fitsInViewport(top, left) && !overlapsTarget(top, left)) {
        return {
          position: "fixed",
          top: `${top}px`,
          left: `${left}px`,
          width: `${tooltipWidth}px`,
          zIndex: 100001,
          transition: "all 0.15s ease-out",
        };
      }
    }

    // Fallback: pick the placement with minimum overlap area with the target
    let bestPlacement = preferred;
    let minOverlap = Infinity;
    for (const placement of placements) {
      const { top, left } = computePosition(placement);
      if (!overlapsTarget(top, left)) {
        bestPlacement = placement;
        break;
      }
      // Compute overlap area
      const tRight = left + tooltipWidth;
      const tBottom = top + tooltipHeight;
      const sLeft = targetRect.left - 8;
      const sTop = targetRect.top - 8;
      const sRight = targetRect.left + targetRect.width + 8;
      const sBottom = targetRect.top + targetRect.height + 8;
      const overlapW = Math.max(0, Math.min(tRight, sRight) - Math.max(left, sLeft));
      const overlapH = Math.max(0, Math.min(tBottom, sBottom) - Math.max(top, sTop));
      const overlapArea = overlapW * overlapH;
      if (overlapArea < minOverlap) {
        minOverlap = overlapArea;
        bestPlacement = placement;
      }
    }

    const { top, left } = computePosition(bestPlacement);
    return {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      zIndex: 100001,
      transition: "all 0.15s ease-out",
    };
  };

  const isLastStep = activeStep === steps.length - 1;
  const maskId = `onboarding-mask-${activeStep}`;

  return (
    <Box className="onboarding-overlay-container">
      {/* SVG Spotlight backdrop mask */}
      <svg className="onboarding-svg-backdrop">
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="10"
                ry="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15, 23, 42, 0.65)"
          mask={`url(#${maskId})`}
          style={{ pointerEvents: "auto" }}
        />
      </svg>

      {/* Target pulsing border layer */}
      {targetRect && (
        <Box
          className="onboarding-spotlight-border"
          sx={{
            top: `${targetRect.top - 6}px`,
            left: `${targetRect.left - 6}px`,
            width: `${targetRect.width + 12}px`,
            height: `${targetRect.height + 12}px`,
          }}
        />
      )}

      {/* Popover Tooltip Card */}
      <Card ref={tooltipRef} className="onboarding-tooltip-card" sx={getTooltipStyle()} elevation={4}>
        <Box className="onboarding-header">
          <Typography className="onboarding-progress-badge">
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <IconButton size="small" onClick={skipTour} sx={{ color: "#777" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography className="onboarding-title" variant="h6">
          {currentStep.title}
        </Typography>

        <Typography className="onboarding-description" variant="body2">
          {currentStep.description}
        </Typography>

        <Box className="onboarding-footer">
          <Button size="small" className="onboarding-btn-skip" onClick={skipTour}>
            Skip Tour
          </Button>

          <Box className="onboarding-actions-right">
            {activeStep > 0 && (
              <Button size="small" className="onboarding-btn-back" onClick={prevStep}>
                Back
              </Button>
            )}
            <Button size="small" variant="contained" className="onboarding-btn-next" onClick={nextStep}>
              {isLastStep ? "Finish" : "Next"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default OnboardingTour;
